import { Dialog, Transition } from '@headlessui/react';
import { useFetcher, useNavigation } from '@remix-run/react';
import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '~/components/Button';

import useEmail from '~/hooks/useEmail';
import usePublication from '~/hooks/usePublication';

import { usePageContext } from '~/context/usePageContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  recommendation_ids: string[];
  successMessage: string;
}

export default function SubmitModal(props: Props) {
  const publication = usePublication();
  const transition = useNavigation();
  const { visitToken } = usePageContext();
  const [email, setEmail] = useEmail();
  const { t } = useTranslation();

  const isSubmitting = transition.state === 'submitting';
  const submittedEmail = transition.submission?.formData.get('email');

  const fetcher = useFetcher();

  useEffect(() => {
    if (isSubmitting) {
      setEmail(submittedEmail ? String(submittedEmail) : '');
    }
  }, [isSubmitting, setEmail, submittedEmail]);

  return (
    <Transition.Root show={props.isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-10 overflow-y-auto" onClose={props.onClose}>
        <div className="flex items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <fetcher.Form
              className={
                'inline-block transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle'
              }
              action="/recommendations_subscribe_form"
              method="post"
            >
              <input type="hidden" name="visit_token" defaultValue={visitToken} />
              <input name="recommendations_success_message" type="hidden" defaultValue={props.successMessage} />
              <input type="hidden" name="publication_id" defaultValue={publication.id} />
              {props.recommendation_ids.map((id) => (
                <input key={id} type="hidden" name="recommendation_ids[]" value={id} />
              ))}
              <div className="space-y-6 rounded-t-lg bg-white px-4 py-6 sm:p-6">
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {`${t('recommendations.one_last_thing')}...`}
                  </h3>
                </div>
                <div className="space-y-6">
                  <div>
                    <div className="space-y-4">
                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder={t('subscribe.email_placeholder') || ''}
                        className="block w-full rounded-wt border-2 border-wt-primary bg-white px-5 py-3 text-base font-light text-black placeholder-gray-400 focus:border-wt-primary focus:outline-none"
                        required
                        defaultValue={email}
                      />
                      <div className="relative flex items-start">
                        <div className="flex h-5 items-center">
                          <input
                            name="include_current_publication"
                            id="include_current_publication"
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            defaultChecked
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="include_current_publication" className="font-medium text-gray-700">
                            {t('recommendations.opt_in_text', {
                              name: publication.name,
                            })}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="align-items-center flex justify-end rounded-b-lg bg-gray-50 px-4 py-3 text-right sm:px-6">
                <Button type="button" className="mx-2" onClick={props.onClose} variant="ghost" disabled={isSubmitting}>
                  {t('shared.cta.cancel')}
                </Button>
                <Button variant="primary" type="submit" isLoading={isSubmitting}>
                  {t('shared.cta.subscribe')}
                </Button>
              </div>
            </fetcher.Form>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
